/**
 * Inactivity Timer Hook
 *
 * Uses an absolute wall-clock deadline so countdown stays accurate across
 * background tab throttling, focus changes, and async logout.
 */

import { useState, useEffect, useCallback, useRef } from 'react';

export const useInactivityTimer = ({
  timeoutMinutes = 30,
  warningMinutes = 5,
  onWarning,
  onTimeout,
  onActivity,
  enabled = true,
}) => {
  const [isWarningVisible, setIsWarningVisible] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const timeoutRef = useRef(null);
  const warningTimeoutRef = useRef(null);
  const countdownRef = useRef(null);
  const deadlineRef = useRef(0);
  const isWarningVisibleRef = useRef(false);
  const logoutStartedRef = useRef(false);
  const enabledRef = useRef(enabled);

  const onWarningRef = useRef(onWarning);
  const onTimeoutRef = useRef(onTimeout);
  const onActivityRef = useRef(onActivity);

  onWarningRef.current = onWarning;
  onTimeoutRef.current = onTimeout;
  onActivityRef.current = onActivity;
  enabledRef.current = enabled;

  const timeoutMs = Math.max(0, Number(timeoutMinutes) || 0) * 60 * 1000;
  const warningMs = Math.max(0, Math.min(Number(warningMinutes) || 0, Number(timeoutMinutes) || 0)) * 60 * 1000;

  const clearScheduledTimers = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
      warningTimeoutRef.current = null;
    }
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
  }, []);

  const hideWarning = useCallback(() => {
    isWarningVisibleRef.current = false;
    setIsWarningVisible(false);
    setTimeRemaining(0);
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
  }, []);

  const getRemainingMs = useCallback(() => {
    if (!deadlineRef.current) {
      return 0;
    }
    return Math.max(0, deadlineRef.current - Date.now());
  }, []);

  const triggerTimeout = useCallback(() => {
    if (logoutStartedRef.current) {
      return;
    }
    logoutStartedRef.current = true;
    clearScheduledTimers();
    hideWarning();
    setIsLoggingOut(true);
    onTimeoutRef.current?.();
  }, [clearScheduledTimers, hideWarning]);

  const startWarningCountdown = useCallback(() => {
    const tick = () => {
      if (logoutStartedRef.current || !enabledRef.current) {
        return 0;
      }

      const remaining = getRemainingMs();
      if (remaining <= 0) {
        triggerTimeout();
        return 0;
      }
      return remaining;
    };

    setTimeRemaining(tick());

    if (countdownRef.current) {
      clearInterval(countdownRef.current);
    }

    countdownRef.current = setInterval(() => {
      setTimeRemaining(tick());
    }, 200);
  }, [getRemainingMs, triggerTimeout]);

  const showWarning = useCallback(() => {
    if (logoutStartedRef.current || !enabledRef.current) {
      return;
    }

    const remaining = getRemainingMs();
    if (remaining <= 0) {
      triggerTimeout();
      return;
    }

    const wasVisible = isWarningVisibleRef.current;
    isWarningVisibleRef.current = true;
    setIsWarningVisible(true);
    startWarningCountdown();

    if (!wasVisible) {
      onWarningRef.current?.();
    }
  }, [getRemainingMs, startWarningCountdown, triggerTimeout]);

  const armTimersFromDeadline = useCallback(() => {
    clearScheduledTimers();

    if (logoutStartedRef.current || !enabledRef.current || !deadlineRef.current) {
      return;
    }

    const remainingMs = getRemainingMs();
    if (remainingMs <= 0) {
      triggerTimeout();
      return;
    }

    const msUntilWarning = Math.max(0, remainingMs - warningMs);

    if (warningMs > 0 && remainingMs <= warningMs) {
      showWarning();
    } else if (warningMs > 0) {
      warningTimeoutRef.current = setTimeout(() => {
        showWarning();
      }, msUntilWarning);
    }

    timeoutRef.current = setTimeout(() => {
      triggerTimeout();
    }, remainingMs);
  }, [clearScheduledTimers, getRemainingMs, showWarning, triggerTimeout, warningMs]);

  const resetTimers = useCallback(() => {
    if (!enabledRef.current || logoutStartedRef.current) {
      return;
    }

    deadlineRef.current = Date.now() + timeoutMs;
    hideWarning();
    armTimersFromDeadline();
    onActivityRef.current?.();
  }, [armTimersFromDeadline, hideWarning, timeoutMs]);

  const handleActivity = useCallback(() => {
    if (!enabledRef.current || logoutStartedRef.current) {
      return;
    }

    // Warning modal is open — only "Stay logged in" may extend the session.
    if (isWarningVisibleRef.current) {
      return;
    }

    resetTimers();
  }, [resetTimers]);

  const syncFromWallClock = useCallback(() => {
    if (!enabledRef.current || logoutStartedRef.current) {
      return;
    }

    const remainingMs = getRemainingMs();
    if (remainingMs <= 0) {
      triggerTimeout();
      return;
    }

    if (warningMs > 0 && remainingMs <= warningMs) {
      showWarning();
    }

    armTimersFromDeadline();
  }, [armTimersFromDeadline, getRemainingMs, showWarning, triggerTimeout, warningMs]);

  const extendSession = useCallback(() => {
    if (!enabledRef.current) {
      return;
    }
    // Allow stay-logged-in only before logout has started
    if (logoutStartedRef.current && isLoggingOut) {
      return;
    }
    logoutStartedRef.current = false;
    setIsLoggingOut(false);
    resetTimers();
  }, [isLoggingOut, resetTimers]);

  const forceLogout = useCallback(() => {
    triggerTimeout();
  }, [triggerTimeout]);

  // Enable / disable lifecycle — keep deps minimal to avoid restarting mid-session
  useEffect(() => {
    if (!enabled || timeoutMs <= 0) {
      clearScheduledTimers();
      hideWarning();
      logoutStartedRef.current = false;
      setIsLoggingOut(false);
      deadlineRef.current = 0;
      return undefined;
    }

    logoutStartedRef.current = false;
    setIsLoggingOut(false);
    deadlineRef.current = Date.now() + timeoutMs;
    armTimersFromDeadline();

    const activityEvents = [
      'mousedown',
      'mousemove',
      'keydown',
      'scroll',
      'touchstart',
      'click',
      'wheel',
    ];

    let lastActivityAt = 0;
    const handleActivityEvent = (event) => {
      if (event.type === 'mousemove' && event.movementX === 0 && event.movementY === 0) {
        return;
      }
      // Throttle activity resets to once per second
      const now = Date.now();
      if (now - lastActivityAt < 1000) {
        return;
      }
      lastActivityAt = now;
      handleActivity();
    };

    activityEvents.forEach((event) => {
      document.addEventListener(event, handleActivityEvent, { passive: true });
    });

    const handleVisibilityOrFocus = () => {
      if (document.visibilityState === 'visible') {
        syncFromWallClock();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityOrFocus);
    window.addEventListener('focus', handleVisibilityOrFocus);
    window.addEventListener('pageshow', handleVisibilityOrFocus);

    return () => {
      activityEvents.forEach((event) => {
        document.removeEventListener(event, handleActivityEvent);
      });
      document.removeEventListener('visibilitychange', handleVisibilityOrFocus);
      window.removeEventListener('focus', handleVisibilityOrFocus);
      window.removeEventListener('pageshow', handleVisibilityOrFocus);
      clearScheduledTimers();
      hideWarning();
    };
    // Intentionally only re-arm when enablement or durations change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, timeoutMs, warningMs]);

  const formatTimeRemaining = useCallback((milliseconds) => {
    const totalSeconds = Math.max(0, Math.ceil(milliseconds / 1000));
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }, []);

  return {
    isWarningVisible: isWarningVisible && enabled && !isLoggingOut,
    timeRemaining,
    formattedTimeRemaining: formatTimeRemaining(timeRemaining),
    warningDurationMs: warningMs || timeoutMs,
    isLoggingOut,
    extendSession,
    forceLogout,
    resetTimers,
  };
};
